import { getProfileRoute } from '../../utils/profileRoute';
// TopBar.tsx — Co Marca Admin — con notificaciones persistentes
// Ruta de detalle cambiada a '/reservas/detalle/:id'

import React, { useState, useEffect, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { navItems } from '../admin/adminNavigation';
import ViewportPopover from '../common/ViewportPopover';
import AdminSearch from '../admin/AdminSearch';
import {AdminAppearanceButton, useAdminAppearance} from '../admin/AdminDesignScope';
import { Bell, User, LogOut, LayoutGrid, ChevronRight, X, CheckCheck, Menu, Sun, Moon } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { logout } from '../../services/authService.ts';
import Loading from '../Loading.tsx';
import { useAuth } from '../../context/AuthContext.tsx';
import { useTheme } from '../../context/ThemeContext.tsx';
import { useWebSocketCitas } from '../../hooks/useWebSocket.ts';
import { fetchNotificaciones, updateNotificacion } from '../../services/notificacionService.ts';
import { getUserRingTier, TIER_CONFIGS, getDynamicTierLabel } from '../../utils/userTier.ts';

import logo from '../../assets/images/logo/logo.png';
const logoDark = `${process.env.PUBLIC_URL}/logo-comarca-blanco.png?v=2`;

interface Notificacion {
    id: number;
    citaId: number;
    codigo: string;
    servicio: string;
    fechaInicio: string;
    horaInicio: string;
    total: string | number;
    estado: string;
    leida: boolean;
    timestamp: Date;
}

const formatRelativeTime = (date: Date): string => {
    const diff = Math.floor((Date.now() - date.getTime()) / 1000);
    if (diff < 60) return 'Ahora mismo';
    if (diff < 3600) return `Hace ${Math.floor(diff / 60)} min`;
    if (diff < 86400) return `Hace ${Math.floor(diff / 3600)} h`;
    return date.toLocaleDateString('es-ES', { day: 'numeric', month: 'short' });
};

const TopBar = () => {
    const [menuOpen, setMenuOpen] = useState(false);
    const [notifOpen, setNotifOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const {setMobileOpen,openSettings}=useAdminAppearance();
    const [notificaciones, setNotificaciones] = useState<Notificacion[]>([]);

    const navigate = useNavigate();
    const { user } = useAuth();
    const { isDark, toggleTheme } = useTheme();

    const noLeidas = notificaciones.filter(n => !n.leida).length;

    // ── Cargar notificaciones desde la API al montar ──
    const loadNotificaciones = useCallback(async () => {
        if (!user) return;
        try {
            const data = await fetchNotificaciones();
            const notifs: Notificacion[] = data.map(notif => ({
                id: notif.id,
                citaId: notif.cita.id,
                codigo: notif.cita.codigo || `CITA-${notif.cita.id}`,
                servicio: notif.cita.servicio_info?.nombre || notif.cita.espacio_en_uso || 'Nueva reserva',
                fechaInicio: notif.cita.fecha_inicio || '',
                horaInicio: notif.cita.hora_inicio || '',
                total: notif.cita.total || 0,
                estado: notif.cita.estado || 'Confirmada',
                leida: notif.leida,
                timestamp: new Date(notif.fecha_creacion),
            }));
            setNotificaciones(notifs);
        } catch (error) {
            /* Browser diagnostics omitted. */
        }
    }, [user]);

    useEffect(() => {
        loadNotificaciones();
    }, [loadNotificaciones]);

    // ── WebSocket: al crear una nueva cita, recargar notificaciones ──
    const handleCitaEvent = useCallback(async (action: string, citaData: any) => {
        if (action === 'create') {
            await loadNotificaciones();
        }
    }, [loadNotificaciones]);

    useWebSocketCitas(handleCitaEvent);

    // ── Cerrar dropdowns al click fuera ──
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            const target = event.target as HTMLElement;
            if (!target.closest('[data-dropdown-trigger]') && !target.closest('[data-dropdown-content]')) {
                setMenuOpen(false);
                setNotifOpen(false);
            }
        };
        document.addEventListener('click', handleClickOutside);
        return () => document.removeEventListener('click', handleClickOutside);
    }, []);

    const handleLogout = async () => {
        setLoading(true);
        await logout();
        setLoading(false);
        navigate('/');
    };

    const marcarLeida = async (id: number) => {
        try {
            await updateNotificacion(id, { leida: true });
            setNotificaciones(prev =>
                prev.map(n => (n.id === id ? { ...n, leida: true } : n))
            );
        } catch (error) {
            /* Browser diagnostics omitted. */
        }
    };

    const marcarTodasLeidas = async () => {
        const noLeidasList = notificaciones.filter(n => !n.leida);
        for (const notif of noLeidasList) {
            try {
                await updateNotificacion(notif.id, { leida: true });
            } catch (error) {
                /* Browser diagnostics omitted. */
            }
        }
        setNotificaciones(prev => prev.map(n => ({ ...n, leida: true })));
    };

    const eliminarNotif = (id: number, e: React.MouseEvent) => {
        e.stopPropagation();
        setNotificaciones(prev => prev.filter(n => n.id !== id));
    };

    const [, setTierTick] = useState(0);
    useEffect(() => {
        const onStorage = () => setTierTick(t => t + 1);
        window.addEventListener('storage', onStorage);
        return () => window.removeEventListener('storage', onStorage);
    }, []);

    const {pathname}=useLocation();
    const activeGroup=navItems.find(group=>group.links.some(link=>pathname===link.href||pathname.startsWith(link.href+'/')));
    const activeLink=activeGroup?.links.find(link=>pathname===link.href)||activeGroup?.links[0];
    const initials = user
        ? `${user.first_name?.[0] ?? ''}${user.last_name?.[0] ?? ''}`.toUpperCase()
        : 'U';
    const userTier = getUserRingTier(user);
    const tierConfig = TIER_CONFIGS[userTier] || TIER_CONFIGS.comun;

    return <>
        <header className="vx-header app-header">
            <button type="button" className="vx-icon-button vx-mobile-toggle" onClick={()=>setMobileOpen(true)} aria-label="Abrir menú principal"><Menu size={21}/></button>
            <Link to="/dashboard" className="vx-header-brand"><img src={`${process.env.PUBLIC_URL}/cowork/emblema.png`} alt=""/><span className="mobile-brand">CO MARCA</span></Link>
            <div className="luma-breadcrumb"><Link to="/dashboard">Co Marca</Link><span>/ {activeGroup?.label||'Administración'} / <b>{activeLink?.label||'Detalle'}</b></span></div><div className="luma-header-search"><AdminSearch/></div>
            <div className="vx-header-actions">
                <button type="button" className="vx-icon-button" onClick={toggleTheme} aria-label={isDark?'Activar modo claro':'Activar modo oscuro'}>{isDark?<Sun size={19}/>:<Moon size={19}/>}</button>
                <AdminAppearanceButton/>
                <button type="button" data-dropdown-trigger="notif" className="vx-icon-button vx-notification-trigger" aria-label={`Notificaciones${noLeidas?`, ${noLeidas} sin leer`:''}`} aria-expanded={notifOpen} onClick={()=>{setNotifOpen(v=>!v);setMenuOpen(false);}}><Bell size={19}/>{noLeidas>0&&<span>{noLeidas>9?'9+':noLeidas}</span>}</button>
                <button type="button" data-dropdown-trigger="user-menu" className="vx-user-trigger" aria-label="Menú de usuario" aria-expanded={menuOpen} onClick={()=>{setMenuOpen(v=>!v);setNotifOpen(false);}}>
                    <span className="luma-avatar">{initials}</span>
                    <span><strong>{user?`${user.first_name} ${user.last_name}`:'Usuario'}</strong><small>{getDynamicTierLabel(user,tierConfig)}</small></span><ChevronRight size={14}/>
                </button>
            </div>
        </header>
        {notifOpen&&<ViewportPopover anchor={()=>document.querySelector('[data-dropdown-trigger="notif"]')} onClose={()=>setNotifOpen(false)} width={420} className="vx-popover vx-notifications" data-dropdown-content="notif">
            <header><h2>Notificaciones <small>{noLeidas} sin leer</small></h2><button type="button" className="vx-icon-button" onClick={()=>setNotifOpen(false)} aria-label="Cerrar notificaciones"><X size={17}/></button></header>
            {noLeidas>0&&<button type="button" className="vx-text-button" onClick={marcarTodasLeidas}><CheckCheck size={15}/>Marcar todas como leídas</button>}
            <div className="vx-notification-list">{notificaciones.length===0?<div className="vx-empty"><Bell size={26}/><strong>Sin notificaciones</strong><p>Las nuevas reservas aparecerán aquí.</p></div>:notificaciones.map(notif=><article className={notif.leida?'':'unread'} key={notif.id}><button type="button" onClick={()=>{void marcarLeida(notif.id);navigate(`/reservas/detalle/${notif.citaId}`);setNotifOpen(false);}}><span className="vx-notification-icon"><Bell size={17}/></span><span><strong>{notif.codigo} · {notif.servicio}</strong><small>{notif.fechaInicio} {notif.horaInicio?.slice(0,5)} · {notif.estado}</small><small>{new Intl.NumberFormat('es-BO',{style:'currency',currency:'BOB'}).format(Number(notif.total)||0)} · {formatRelativeTime(notif.timestamp)}</small></span></button><button type="button" className="vx-icon-button" aria-label={`Descartar notificación ${notif.codigo}`} onClick={e=>eliminarNotif(notif.id,e)}><X size={14}/></button></article>)}</div>
            <footer><Link to="/reservas" onClick={()=>setNotifOpen(false)}>Ver todas las reservas<ChevronRight size={14}/></Link></footer>
        </ViewportPopover>}
        {menuOpen&&<ViewportPopover anchor={()=>document.querySelector('[data-dropdown-trigger="user-menu"]')} onClose={()=>setMenuOpen(false)} className="vx-popover vx-user-menu" data-dropdown-content="user-menu"><header><div><strong>{user?.first_name} {user?.last_name}</strong><small>{getDynamicTierLabel(user,tierConfig)}</small></div></header><Link to={getProfileRoute(user)} onClick={()=>setMenuOpen(false)}><User size={17}/>Mi perfil</Link><button type="button" onClick={()=>{setMenuOpen(false);openSettings();}}><PaletteIcon/>Apariencia del sistema</button><Link to="/" onClick={()=>setMenuOpen(false)}><LayoutGrid size={17}/>Volver al inicio</Link><button type="button" className="vx-danger" disabled={loading} onClick={handleLogout}><LogOut size={17}/>Cerrar sesión</button></ViewportPopover>}
        {loading&&<Loading text="Cerrando sesión..." fullScreen/>}
    </>;
};
const PaletteIcon=()=> <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M12 3a9 9 0 1 0 0 18h1a3 3 0 0 0 2-5h-1a1 1 0 0 1 0-2h2a5 5 0 0 0 0-10z"/><circle cx="7" cy="10" r="1"/><circle cx="10" cy="7" r="1"/><circle cx="15" cy="7" r="1"/></svg>;
export default TopBar;

